/**
 * Party window (registry id `party`) on the Tome page (Tome UI Round 5, E4):
 * WindowHost draws the frame, title and eyebrow (playerWindowPresentation.ts);
 * this is the page — "Your party" (you and the leader marked, vitals, Leave,
 * leader-only Remove) and "Nearby players" with Invite. Incoming invites
 * answer in the HUD's PartyInvitePrompt, open window or not.
 */
import React from 'react';
import { useGame } from '../contexts/GameContext';
import { useParty, type PartyMember } from '../hooks/useParty';
import { JOB_ADVANCEMENTS } from '../../../shared/classes/JobAdvancements';
import { ProgressBar } from '../primitives/ProgressBar';
import { TomeButton, TomeChapter, TomeChip } from '../primitives/tome';
import { COLORS, PLAYER_UI, formatCallingName } from '../theme';
import { SocialIdentity } from './Social/SocialIdentity';
import './Social/SocialPresentation.css';
import './PartyPanel.css';

const COMPACT = { minHeight: 34, padding: '0 14px', fontSize: 13 } as const;

function callingOf(member: PartyMember): string {
  const job = JOB_ADVANCEMENTS[member.job as keyof typeof JOB_ADVANCEMENTS];
  if (!job) return 'Calling unavailable';
  return job.requiredLevel <= 1 ? formatCallingName(job.requiredClass) : job.name;
}

const Vital: React.FC<{ label: string; value: number; max: number; color: string }> = ({ label, value, max, color }) => <>
  <div className="party-tome-resource"><span>{label}</span><span>{Math.round(value)} / {Math.round(max)}</span></div>
  <ProgressBar value={value} max={max} showPercentage={false} height={7} customColor={color} />
</>;

export const PartyContent: React.FC = () => {
  const { player, otherPlayers } = useGame();
  const { partyId, members, isLeader, invite, leave, kick } = useParty();
  const inParty = partyId !== null && members.length > 0;
  const nearby = Array.from(otherPlayers.values());
  return <section className="party-tome" data-testid="party-content" aria-label="Party roster">
    {inParty && <TomeChapter title="Your party" count={members.length} tone="ready">
      {members.map(member => {
        const self = player?.id === member.sessionId;
        const actor = otherPlayers.get(member.sessionId) ?? (self ? player : undefined);
        return <article key={member.sessionId} className="party-tome-card" data-testid={`party-member-${member.sessionId}`}
          data-self={self || undefined} data-leader={member.isLeader || undefined}>
          <div className="party-tome-row">
            <SocialIdentity actor={actor} name={member.name} level={member.level} size={48} />
            <div className="party-tome-marks">
              {self && <TomeChip tone="active">You</TomeChip>}
              {member.isLeader && <TomeChip tone="available">Leader</TomeChip>}
              {isLeader && !member.isLeader && <TomeButton tone="secondary" className="party-tome-remove" style={COMPACT}
                onClick={() => kick(member.sessionId)} aria-label={`Remove ${member.name} from party`}>Remove</TomeButton>}
            </div>
          </div>
          {/* The identity line names the class when the player is in view; repeat it only when this adds something (a job). */}
          {callingOf(member) !== (actor?.class ? formatCallingName(actor.class) : null) && <p className="party-tome-calling">{callingOf(member)}</p>}
          <div className="party-tome-vitals">
            <Vital label="Health" value={member.health} max={member.maxHealth} color={PLAYER_UI.danger} />
            <Vital label="Mana" value={member.mana} max={member.maxMana} color={COLORS.mana} />
          </div>
        </article>;
      })}
      <div className="party-tome-actions">
        <TomeButton tone="secondary" className="party-tome-leave" style={COMPACT} onClick={leave}>Leave Party</TomeButton>
      </div>
    </TomeChapter>}
    <TomeChapter title="Nearby players" count={nearby.length || undefined}>
      {nearby.length ? nearby.map(other => {
        const alreadyInParty = members.some(member => member.sessionId === other.id);
        return <article key={other.id} className="party-tome-card party-tome-row">
          <SocialIdentity actor={other} name={other.name} level={other.level} size={44} />
          <TomeButton tone={alreadyInParty ? 'secondary' : 'primary'} style={COMPACT} onClick={() => invite(other.id)}
            disabled={alreadyInParty} aria-label={`${alreadyInParty ? 'Already in party' : 'Invite'} ${other.name}`}>
            {alreadyInParty ? 'In Party' : 'Invite'}</TomeButton>
        </article>;
      }) : <p className="party-tome-empty">No other players nearby. Meet another adventurer to form a party.</p>}
    </TomeChapter>
  </section>;
};
